import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { BDAddendumAgreementForm } from '../forms'
import { useHistory, Redirect } from 'react-router-dom'
import {
  Path,
  AccountAudience,
  FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
} from 'config/portal'
import {
  TOAST_ERROR_CONTACT_SUPPORT,
  AddendumSuccessMessages,
} from 'config/notifications'
import * as apiActions from 'api-actions'
import * as productActions from '../../../products/actions'
import { selectors as globalSelectors } from 'global-reducer'
import { flashErrorMessage, flashSuccessMessage } from 'redux-flash'
import { selectors as selectors } from '../../reducer'
import { selectors as productSelectors } from '../../../products/reducer'
import { isEmpty } from 'lodash'
import { ComponentFailure } from 'components'
import * as actions from '../../../automatic-rollovers/actions'
import { SigningMethod, signingMethodOptions } from 'config/automatic-rollovers'
import { ProductAgreementType } from 'config/products'

const propTypes = {
  currentUser: Types.user.isRequired,
  addendumServicesAgreementHash: PropTypes.object.isRequired,
  submitAddendumServicesAgreementForm: PropTypes.func.isRequired,
  setShouldTriggerAllServicesAgreementsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllBDPlanAgreementsFetch: PropTypes.func.isRequired,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  completeServicesAgreementForm: Types.completeAddendumForm,
  fetchServicesAgreementDetails: PropTypes.func.isRequired,
  setCompleteServicesAgreementForm: PropTypes.func.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
}

const defaultProps = {
  completedServicesAgreements: null,
}
const electronicSignatureOption = signingMethodOptions.find(
  (option) => option.key === SigningMethod.ELECTRONIC_SIGNATURE
)
const wetSignatureOption = signingMethodOptions.find(
  (option) => option.key === SigningMethod.WET_SIGNATURE
)
const generateSuccessMessageBD = (signingMethod) => {
  if (signingMethod === electronicSignatureOption.value) {
    return AddendumSuccessMessages.ELECTRONIC_SIGNATURE
  } else if (signingMethod === wetSignatureOption.value) {
    return AddendumSuccessMessages.WET_SIGNATURE
  } else {
    return AddendumSuccessMessages.E_SIGN
  }
}

function ReviewAndSubmitAddendumView({
  currentUser,
  submitAddendumServicesAgreementForm,
  setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch,
  flashErrorMessageHandler,
  addendumServicesAgreementHash,
  completeServicesAgreementForm,
  fetchServicesAgreementDetails,
  setCompleteServicesAgreementForm,
  flashSuccessMessageHandler,
}) {
  const history = useHistory()
  const [error, setError] = useState(false)
  const [planAgreementDetails, setPlanAgreemantsDetails] = useState({})
  const isPlanFiduciary =
    currentUser.accountAudience === AccountAudience.PLAN_FIDUCIARY

  const addendumAgreement = addendumServicesAgreementHash
  const servicesAgreementId = addendumAgreement.value

  useEffect(() => {
    if (!completeServicesAgreementForm?.signingMethod && isPlanFiduciary) {
      setCompleteServicesAgreementForm({ signingMethod: 'eSignRSP' })
    }
  })

  useEffect(() => {
    async function getPlanAgreementDetails() {
      try {
        const response = await fetchServicesAgreementDetails(
          servicesAgreementId
        )
        setPlanAgreemantsDetails(response)
      } catch {
        setError(true)
      }
    }
    if (!isPlanFiduciary) getPlanAgreementDetails()
  }, [servicesAgreementId])

  const planSponsorCompanyDetails = planAgreementDetails
    ? planAgreementDetails.planSponsorCompany
    : null
  const planSponsorContactDetails = planAgreementDetails
    ? planAgreementDetails.planSponsorContact
    : null

  if (error) return <ComponentFailure />

  const getSigningMethod = (signingMethod) => {
    switch (signingMethod) {
      case 'wetSignature':
        return 'generateDocument'
      case 'electronicSignature':
        return 'Electronic'
      case 'eSignRSP':
        return 'E-Sign RSP'
      default:
        return ''
    }
  }

  const handleSubmit = async () => {
    try {
      if (addendumAgreement != null) {
        // This Condition should be uncommented once the Addendum data flow form Add Addendum Flow is complete.
        await submitAddendumServicesAgreementForm({
          signerEmail: currentUser.userEmail,
          signerTitle: currentUser.userTitle,
          addendumStatus: 'Signed',
          signerName: currentUser.firstName,
          signingMethod: getSigningMethod(
            completeServicesAgreementForm.signingMethod
          ),
          serviceAgreementId: addendumAgreement.value, // selectedAddendumAgreement.serviceAgreementID, to be added after the data flow fpr Addendum is completed.
          portalAddendumType: ProductAgreementType.BD,
        })
        setShouldTriggerAllServicesAgreementsFetch(true)
        setShouldTriggerAllBDPlanAgreementsFetch(true)
        history.push({
          pathname: Path.BD_SERVICES_AGREEMENTS,
          state: {
            ignorePrompt: true, // No need to warn the user about leaving the flow
          },
        })
        flashSuccessMessageHandler(
          generateSuccessMessageBD(completeServicesAgreementForm.signingMethod),
          { timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT }
        )
      } else {
        //// This Condition should be uncommented once the Addendum data flow form Add Addendum Flow is complete.
        flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT) //// This Condition should be uncommented once the Addendum data flow form Add Addendum Flow is complete.
      } //// This Condition should be uncommented once the Addendum data flow form Add Addendum Flow is complete.
    } catch (error) {
      flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    }
  }

  if (isEmpty(completeServicesAgreementForm) && !isPlanFiduciary)
    return <Redirect to={Path.ADD_NEW_BD_ADDENDUM} />
  if (isEmpty(addendumServicesAgreementHash))
    return <Redirect to={Path.ADD_NEW_BD_ADDENDUM} />

  return (
    <div className="card full-height complete-search-agreement-view">
      <div className="complete-search-agreement-form-container">
        <div className="form-masthead">
          {isPlanFiduciary ? (
            <h2>Next, review and agree to the addendum details</h2>
          ) : (
            <>
              <h2>Confirm your plan sponsor contact information</h2>
              <p className="form-masthead-text">
                Check that your plan sponsor's contact information is correct.
              </p>
            </>
          )}
        </div>
        <div className="form-section">
          <BDAddendumAgreementForm
            initialValues={{
              confirm: false,
              dialogRead: false,
            }}
            onSubmit={handleSubmit}
            isPlanFiduciary={isPlanFiduciary}
            userDetails={{
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              title: currentUser.userTitle,
              phone: currentUser.accountPhone,
              email: currentUser.userEmail,
            }}
            companyDetails={planSponsorCompanyDetails}
            contactDetails={planSponsorContactDetails}
          />
        </div>
      </div>
    </div>
  )
}

ReviewAndSubmitAddendumView.propTypes = propTypes
ReviewAndSubmitAddendumView.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentUser: globalSelectors.currentUser(state),
    shouldTriggerAllServicesAgreementsFetch:
      productSelectors.shouldTriggerAllServicesAgreementsFetch(state),
    servicesAgreements: productSelectors.servicesAgreements(state),
    addendumServicesAgreementHash:
      selectors.addendumServicesAgreementHash(state),
    completeServicesAgreementForm:
      selectors.completeServicesAgreementForm(state),
  }
}

const mapDispatchToProps = {
  submitAddendumServicesAgreementForm:
    apiActions.submitAddendumServicesAgreementForm,
  setShouldTriggerAllServicesAgreementsFetch:
    productActions.setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch:
    productActions.setShouldTriggerAllBDPlanAgreementsFetch,
  flashErrorMessageHandler: flashErrorMessage,
  fetchServicesAgreementDetails: apiActions.fetchServicesAgreementDetails,
  setCompleteServicesAgreementForm: actions.setCompleteServicesAgreementForm,
  flashSuccessMessageHandler: flashSuccessMessage,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(ReviewAndSubmitAddendumView)
)
